import React, { createContext, useContext, useState, useCallback } from 'react';
import { initialNodes } from '../screens/Configuracoes/Fluxos/Detalhe/inicialValores';
import { Edge, Node, OnConnect, OnEdgesChange, OnNodesChange, addEdge, applyEdgeChanges, applyNodeChanges } from 'reactflow';
import { verificacaoDropItemNoQuadro } from '../screens/Configuracoes/Fluxos/Detalhe/funcoesAuxiliares';
import { enqueueSnackbar } from 'notistack';
import gerarIdRudimentar from '../helpers/gerarIdRudimentar';

const FluxoContext = createContext<IFluxoContext>({
    edges: [],
    nodes: [],
    openModais: null,
    clickedNode: null,
    handleNodeState: () => { },
    handleEdgeState: () => { },
    handleClickState: () => { },
    handleOpenModais: () => { },
    onDragItemOnBoard: () => { },
    onNodesChange: () => { },
    onEdgesChange: () => { },
    onConnect: () => { },
    onNodeClick: () => { },
    onClickDeleteBtn: () => { },
    onRemoveEdge: () => { },
    onNodeDoubleClick: () => { }
});

export const FluxoProvider = ({ children }: IFluxoProvider) => {
    const [edges, setEdges] = useState<TEdge>([]);
    const [nodes, setNodes] = useState<TNode>(initialNodes);
    const [clickedNode, setClickedNode] = useState<TClickedNode>(null);

    const [openModais, setOpenModais] = useState({
        selecaoPerfil: null,
        editCondicionalAuto: null,
    })

    //funções internas de State
    const handleNodeState = (nodes: Node[]) => {
        setNodes(nodes)
    }

    const handleEdgeState = (edges: Edge[]) => {
        setEdges(edges)
    }

    const handleClickState = (value: Node | null) => {
        setClickedNode(value)
    }
    //

    //Funções do Fluxo
    const handleOpenModais = useCallback((prop: keyof TOpenModaisState, value: any) => {
        setOpenModais(prevState => ({ ...prevState, [prop]: value }))
    }, [])

    const onDropPerfilNode = useCallback((item: any) => {
        if (item.type !== "Perfil" && item.type !== "Condicional Automatica") return false

        handleOpenModais(item.data.modal, item)
        return true
    }, [handleOpenModais])

    const onDragItemOnBoard = useCallback((item: any) => {
        const verificacao = verificacaoDropItemNoQuadro(item, nodes)
        if (verificacao) return

        const dropPerfil = onDropPerfilNode(item)
        if (dropPerfil) return

        let newNode = {
            id: gerarIdRudimentar(),
            type: item.type,
            position: item.position,
            data: { label: item.label }
        }
        setNodes((prevNodes) => [...prevNodes, newNode])
    }, [nodes, onDropPerfilNode])

    const onNodesChange: OnNodesChange = useCallback((changes) => {
        setNodes((nds: any) => applyNodeChanges(changes, nds))
    }, [setNodes]);

    const onEdgesChange: OnEdgesChange = useCallback((changes) => {
        setEdges((eds: any) => applyEdgeChanges(changes, eds))
    }, [setEdges]);

    const onConnect: OnConnect = useCallback((connection) => {
        const params = {
            id: gerarIdRudimentar(),
            type: "step",
            style: { strokeWidth: 2 },
            markerEnd: {type: 'arrowclosed'},
            zIndex: 99
        }
        setEdges((eds: any) => addEdge({...connection, ...params}, eds))
    }, [setEdges]);

    const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
        setClickedNode(node)
    }, [])

    const onNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => {
        if (!node.data.modal) return
        handleOpenModais(node.data.modal, node)
    }, [handleOpenModais])

    const onClickDeleteBtn = useCallback(() => {
        if (!clickedNode) return
        if (clickedNode.id === "perfilroot_techpines") {
            enqueueSnackbar("Não é permitido remover o perfil selecionado!", { variant: "warning" })
            return
        }
        const updatedNodes = [...nodes];

        const indexToRemove = updatedNodes.findIndex(node => node.id === clickedNode.id);
        updatedNodes.splice(indexToRemove, 1);

        const updatedEdges = edges.filter(edge => edge.source !== clickedNode.id && edge.target !== clickedNode.id);

        setNodes(updatedNodes);
        setEdges(updatedEdges);
    }, [clickedNode, edges, nodes])

    const onRemoveEdge = useCallback((id: string) => {
        const novoEdges = edges.filter(edge => edge.id !== id)
        setEdges(novoEdges)
    }, [edges])
    //

    return (
        <FluxoContext.Provider
            value={{
                edges,
                nodes,
                openModais,
                clickedNode,
                handleNodeState,
                handleEdgeState,
                handleClickState,
                handleOpenModais,
                onDragItemOnBoard,
                onNodesChange,
                onEdgesChange,
                onConnect,
                onNodeClick,
                onClickDeleteBtn,
                onRemoveEdge,
                onNodeDoubleClick
            }}
        >
            {children}
        </FluxoContext.Provider>
    );
}

export const useFluxo = () => useContext(FluxoContext);

interface IFluxoProvider {
    children: React.ReactNode
}

interface IFluxoContext {
    edges: TEdge;
    nodes: TNode;
    openModais: any;
    clickedNode: TClickedNode;
    handleNodeState: (nodes: TNode) => void;
    handleEdgeState: (nodes: TEdge) => void;
    handleClickState: (value: TClickedNode) => void;
    handleOpenModais: (prop: keyof TOpenModaisState, value: any) => void;
    onDragItemOnBoard: (item: any) => void;
    onNodesChange: OnNodesChange;
    onEdgesChange: OnEdgesChange;
    onConnect: OnConnect;
    onNodeClick: (event: React.MouseEvent, node: Node) => void;
    onClickDeleteBtn: () => void;
    onRemoveEdge: (id: string) => void;
    onNodeDoubleClick: (event: React.MouseEvent, node: Node) => void;
}

type TEdge = Edge[];
type TNode = Node[];
type TClickedNode = Node | null;
type TOpenModaisState = {
    selecaoPerfil: Node | null,
    editCondicionalAuto: Node | null
}

